import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import {
  Button as MUIButton,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { User, Role } from '../types'
import { getUsers, createUser, updateUser, activateUser, deleteUser } from '../services/users'
import UserForm from '../components/users/UserForm'
import YardAssignmentDialog from '../components/users/YardAssignmentDialog'
import { theme } from '../styles/theme'

/**
 * Styled container
 */
const Container = styled.div`
  padding: ${theme.spacing.xl}px;
  max-width: 1200px;
  margin: 0 auto;
`

/**
 * Styled header
 */
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl}px;
`

/**
 * Styled title
 */
const Title = styled.h1`
  font-size: ${theme.typography.fontSize.xxlarge};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
`

/**
 * Styled MUI button
 */
const Button = styled(MUIButton)`
  && {
    padding: ${theme.spacing.md}px ${theme.spacing.lg}px;
    border-radius: ${theme.borderRadius.large}px;
  }
`

/**
 * User management screen component
 * @returns User management screen
 */
function UserManagementScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [yardAssignmentUser, setYardAssignmentUser] = useState<User | null>(null)
  const [showSubAccountForm, setShowSubAccountForm] = useState(false)

  /**
   * Loads users from API
   * @returns Promise void
   */
  async function loadUsers(): Promise<void> {
    try {
      setLoading(true)
      const response = await getUsers()
      setUsers(response.data)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Filters users by search query
   * @returns void
   */
  const filterUsers = useCallback((): void => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users)
      return
    }

    const query = searchQuery.toLowerCase()
    const filtered = users.filter(
      u =>
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        u.role.toLowerCase().includes(query)
    )
    setFilteredUsers(filtered)
  }, [searchQuery, users])

  useEffect(() => {
    loadUsers()
  }, [])

  useEffect(() => {
    filterUsers()
  }, [filterUsers])

  /**
   * Gets available roles for current user
   * @returns Array of available roles
   */
  function getAvailableRoles(): Role[] {
    if (currentUser?.role === 'super_admin') {
      return ['super_admin', 'owner', 'admin', 'user']
    }
    if (currentUser?.role === 'owner') {
      return ['admin', 'user']
    }
    if (currentUser?.role === 'admin') {
      return ['admin', 'user']
    }
    return ['user']
  }

  /**
   * Gets parent account options
   * @returns Parent account options
   */
  function getParentOptions(): Array<{ id: string; name: string }> {
    return users.filter(u => u.role !== 'super_admin').map(u => ({ id: u.id, name: u.name }))
  }

  /**
   * Builds parent lookup map
   * @returns Parent lookup
   */
  function getParentLookup(): Record<string, string> {
    return users.reduce((acc, user) => ({ ...acc, [user.id]: user.name }), {})
  }

  /**
   * Handles user form submission
   * @param userData - User data
   * @returns Promise void
   */
  async function handleSubmit(userData: Partial<User> & { password?: string }): Promise<void> {
    try {
      setIsSubmitting(true)
      if (editingUser) {
        await updateUser(editingUser.id, userData)
      } else {
        await createUser(userData as Partial<User> & { password: string })
      }
      setShowForm(false)
      setEditingUser(null)
      await loadUsers()
    } catch (error: any) {
      alert(error.response?.data?.error || t('users.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handles user activation toggle
   * @param user - User to activate/deactivate
   * @returns Promise void
   */
  async function handleActivate(user: User): Promise<void> {
    const message = user.isActive
      ? t('users.confirmDeactivateUser', { name: user.name })
      : t('users.confirmActivateUser', { name: user.name })
    if (!window.confirm(message)) {
      return
    }

    try {
      await activateUser(user.id, !user.isActive)
      await loadUsers()
    } catch (error: any) {
      alert(error.response?.data?.error || t('users.error'))
    }
  }

  /**
   * Handles user deletion
   * @param user - User to delete
   * @returns Promise void
   */
  async function handleDelete(user: User): Promise<void> {
    if (!window.confirm(t('users.confirmDelete', { name: user.name }))) {
      return
    }

    try {
      await deleteUser(user.id)
      await loadUsers()
    } catch (error: any) {
      alert(error.response?.data?.error || t('users.error'))
    }
  }

  async function handleSubAccountSubmit(userData: Partial<User> & { password?: string }): Promise<void> {
    try {
      setIsSubmitting(true)
      await createUser({
        ...userData,
        role: 'admin',
        parentAccountId: currentUser?.id,
        password: userData.password || '',
      } as Partial<User> & { password: string })
      setShowSubAccountForm(false)
      await loadUsers()
    } catch (error: any) {
      alert(error.response?.data?.error || t('users.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const userPerms = currentUser?.permissions || []
  const canCreate = userPerms.includes('users.create')
  const canEdit = userPerms.includes('users.edit')
  const canActivate = userPerms.includes('users.activate')
  const canDelete = userPerms.includes('users.delete')
  const showParentSelect = currentUser?.role === 'super_admin'

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  if (loading) {
    return <Container>{t('common.loading')}</Container>
  }

  return (
    <Container>
      <Header>
        <Title>{t('users.title')}</Title>
        {canCreate && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="contained" onClick={() => {
              setEditingUser(null)
              setShowForm(true)
            }}>
              {t('users.create')}
            </Button>
            <Button variant="outlined" onClick={() => setShowSubAccountForm(true)}>
              {t('users.createSubAccount')}
            </Button>
          </div>
        )}
      </Header>

      <TextField
        fullWidth
        size="small"
        label={t('users.search')}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        sx={{ maxWidth: 420, marginBottom: theme.spacing.md }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('users.name')}</TableCell>
              <TableCell>{t('users.email')}</TableCell>
              <TableCell>{t('users.phone')}</TableCell>
              <TableCell>{t('users.role')}</TableCell>
              <TableCell>{t('users.status')}</TableCell>
              <TableCell>{t('users.parentAccount')}</TableCell>
              <TableCell>{t('users.lastLogin')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map(user => {
              const parentName = user.parentAccountId 
                ? users.find(u => String(u.id) === String(user.parentAccountId))?.name 
                : null
              
              return (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={t(`users.roles.${user.role}`, { defaultValue: user.role.replace('_', ' ') })}
                      size="small"
                      color={user.role === 'super_admin' ? 'error' : user.role === 'admin' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {user.isActive ? (
                      <Chip icon={<CheckCircleIcon />} label={t('users.active')} size="small" color="success" />
                    ) : (
                      <Chip icon={<CancelIcon />} label={t('users.inactive')} size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell>{parentName || '-'}</TableCell>
                  <TableCell>
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString(i18n.language) : t('users.never')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('accounts.viewDetails')}>
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/user-management/${user.id}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title={t('users.manageYardAccess')}>
                        <IconButton 
                          size="small" 
                          onClick={() => setYardAssignmentUser(user)}
                          color="primary"
                        >
                          <LocationOnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title={t('button.edit.text')}>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingUser(user)
                            setShowForm(true)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canActivate && (
                      <Tooltip title={user.isActive ? t('users.deactivate') : t('users.activate')}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleActivate(user)}
                        >
                          {user.isActive ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title={t('button.delete.text')}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(user)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {paginatedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t('users.noUsersFound')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('users.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('users.displayedRows', { from, to, total: count !== -1 ? count : t('users.moreThan', { value: to }) })}
        />
      </TableContainer>

      <Dialog open={showForm} onClose={() => {
        setShowForm(false)
        setEditingUser(null)
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? t('users.edit') : t('users.create')}</DialogTitle>
        <DialogContent>
          <UserForm
            user={editingUser}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false)
              setEditingUser(null)
            }}
            isLoading={isSubmitting}
            availableRoles={getAvailableRoles()}
            parentOptions={getParentOptions()}
            showParentSelect={currentUser?.role === 'super_admin'}
          />
        </DialogContent>
      </Dialog>

      {yardAssignmentUser && (
        <YardAssignmentDialog
          open={!!yardAssignmentUser}
          onClose={() => setYardAssignmentUser(null)}
          user={yardAssignmentUser}
          onSuccess={() => {
            loadUsers()
            setYardAssignmentUser(null)
          }}
        />
      )}

      <Dialog open={showSubAccountForm} onClose={() => setShowSubAccountForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.createSubAccount')}</DialogTitle>
        <DialogContent>
          <UserForm
            user={null}
            onSubmit={handleSubAccountSubmit}
            onCancel={() => setShowSubAccountForm(false)}
            isLoading={isSubmitting}
            availableRoles={['admin']}
            parentOptions={[]}
            showParentSelect={false}
          />
        </DialogContent>
      </Dialog>
    </Container>
  )
}

export default UserManagementScreen
